import { useEffect, useRef } from 'react';

/**
 * Everything inside the dialog that a Tab can land on. Mirrors GlassModal's FOCUSABLE list:
 * `:not([disabled])` and the negative-tabindex exclusion keep a disabled button or an element
 * deliberately removed from the tab order from becoming the trap's boundary.
 */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * useFocusTrap — keyboard focus trap for HAND-ROLLED modals that don't use GlassModal.
 *
 * GlassModal got this behaviour in 947798abd (finding "modal does not grab focus on open, and
 * Tab escapes the dialog into the page behind"), but modals that render their own
 * `fixed inset-0 z-50` overlay — Customers.js's CustomerForm / CustomerEditForm and
 * AddCustomerModal — never inherited it, so Tab still walked out to the sidebar behind them.
 *
 * Returns a ref to attach to the modal's PANEL element. While `active`, it:
 *  - moves focus into the dialog on open (unless the browser already placed focus inside, e.g.
 *    an `autoFocus` field),
 *  - wraps Tab / Shift+Tab within the dialog,
 *  - restores focus to whatever was focused before, on close.
 *
 * The keydown listener is bound to the PANEL element itself, not `document`, on purpose: these
 * modals put `onKeyDown={(e) => e.stopPropagation()}` on their overlay, which would stop a
 * document-level listener from ever seeing the Tab. A listener on the panel fires during the
 * DOM bubble at the panel, before React's synthetic stopPropagation runs at the root.
 *
 * @param {boolean} active - whether the trap is engaged (default true; pass the modal's `open`
 *   flag for components that stay mounted and return null when closed).
 */
export default function useFocusTrap(active = true) {
  const ref = useRef(null);

  useEffect(() => {
    if (!active) return undefined;
    const panel = ref.current;
    if (!panel) return undefined;

    const handleKeyDown = (e) => {
      if (e.key !== 'Tab') return;

      const focusable = Array.from(panel.querySelectorAll(FOCUSABLE))
        // offsetParent is null for anything display:none — a collapsed section's fields must
        // not become invisible stops in the cycle.
        .filter((el) => el.offsetParent !== null || el === document.activeElement);

      if (focusable.length === 0) {
        // Nothing to focus, but the dialog still must not leak Tab to the page behind it.
        e.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeEl = document.activeElement;

      // Wrap at both ends, and also catch the case where focus is somehow already outside.
      if (e.shiftKey && (activeEl === first || !panel.contains(activeEl))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (activeEl === last || !panel.contains(activeEl))) {
        e.preventDefault();
        first.focus();
      }
    };

    panel.addEventListener('keydown', handleKeyDown);

    // Remember where focus came FROM so it can be put back — otherwise closing the dialog drops
    // the keyboard user at the top of the document.
    const previouslyFocused = document.activeElement;

    // Move focus INTO the dialog. Deferred a frame because children may not be laid out yet.
    // If the browser already focused something inside (an autoFocus field), leave it there.
    const focusTimer = setTimeout(() => {
      if (!ref.current) return;
      if (ref.current.contains(document.activeElement)) return;
      const firstFocusable = ref.current.querySelector(FOCUSABLE);
      (firstFocusable || ref.current).focus?.();
    }, 0);

    return () => {
      clearTimeout(focusTimer);
      panel.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [active]);

  return ref;
}
